import React, { useEffect, useState } from 'react'
import Marquee from 'react-fast-marquee'

import { type HomepageLogo } from '../../types'
import { canHoverWithPointer } from '../../utils/pointer'
import styles from './styles.module.scss'

type PartnersProps = {
  title: string
  partners: HomepageLogo[]
}

const PARTNERS_PER_ROW = 4
// The mobile layout shows three logos at a time; more than that run as a marquee, like the
// "Recently featured on" logos.
const MOBILE_VISIBLE_PARTNERS = 3
const mobileQuery = '(max-width: 767px)'

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(mobileQuery).matches)

  useEffect(() => {
    const mediaQuery = window.matchMedia(mobileQuery)
    const update = () => setIsMobile(mediaQuery.matches)
    mediaQuery.addEventListener('change', update)
    return () => mediaQuery.removeEventListener('change', update)
  }, [])

  return isMobile
}

const PartnerLogo = ({ partner }: { partner: HomepageLogo }) => {
  const mark = partner.image ? (
    <img src={partner.image} alt={`${partner.name} logo`} />
  ) : (
    <span>{partner.name}</span>
  )

  return partner.url ? (
    <a
      className={styles.logo}
      href={partner.url}
      target="_blank"
      rel="noreferrer"
      aria-label={partner.name}
    >
      {mark}
    </a>
  ) : (
    <div className={styles.logo}>{mark}</div>
  )
}

const Partners = ({ title, partners }: PartnersProps) => {
  const isMobile = useIsMobile()
  const [pauseOnPointer] = useState(canHoverWithPointer)

  if (!partners.length) return null

  const rows = Array.from({ length: Math.ceil(partners.length / PARTNERS_PER_ROW) }, (_, index) =>
    partners.slice(index * PARTNERS_PER_ROW, (index + 1) * PARTNERS_PER_ROW)
  )

  return (
    <section className={styles.section} aria-labelledby="partners-title">
      <div className={styles.content}>
        <h2 id="partners-title" className={styles.title}>
          {title}
          <span aria-hidden />
        </h2>
        {isMobile && partners.length > MOBILE_VISIBLE_PARTNERS ? (
          <div className={styles.marqueeViewport} aria-label="Partners">
            <Marquee
              className={styles.marquee}
              autoFill
              pauseOnHover={pauseOnPointer}
              pauseOnClick={pauseOnPointer}
              speed={38}
              gradient={false}
            >
              {partners.map((partner) => (
                <PartnerLogo key={partner.name} partner={partner} />
              ))}
            </Marquee>
          </div>
        ) : (
          <div className={styles.partners}>
            {rows.map((row) => (
              <div key={row[0].name} className={styles.row}>
                {row.map((partner) => (
                  <PartnerLogo key={partner.name} partner={partner} />
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  )
}

export default Partners
